import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form as FormikForm } from 'formik'

import { useConfig } from '@peracto/peracto-config'
import axios from 'axios'
import * as qs from 'query-string'
import { RecaptchaForm } from './RecaptchaForm'

const getDefaultInput = type => {
    switch (type) {
        case 'multi-select':
            return []
        case 'checkbox':
            return false
        default:
            return null
    }
}

const FormGenerator = ({ formData, defaultInputs, hideTitle }) => {
    const config = useConfig()
    const configSettings = config.get('editorConfig')
    const API_URL = config.get('api')

    const inputTypes = configSettings?.inputTypes || {}
    const [errorMessages, setErrorMessages] = useState()
    const [reCaptchaToken, setReCaptchaToken] = useState(null)

    const { Checkbox, CheckboxGroup, Button, Input, Textarea, Select, Radio, File } = {
        ...defaultInputs,
        ...inputTypes,
    }

    const handleSubmit = async (form, data) => {
        setErrorMessages(null)

        const { formFieldLinks } = form

        const values = []
        const formData = new FormData()
        formData.append('form', form['@id'])

        let headers = {
            'Content-Type': 'multipart/form-data',
        }
        if (reCaptchaToken) {
            headers['g-recaptcha-response'] = reCaptchaToken
        }

        for (let [field, value] of Object.entries(data)) {
            if (value !== null) {
                const isFileUpload = formFieldLinks.find(
                    link => field === link['@id'] && link.inputType === 'file'
                )

                if (isFileUpload) {
                    formData.append(field, value)
                } else {
                    values.push({
                        formField: field,
                        fieldValue: value,
                    })
                }
            }
        }

        values.forEach((value, index) => {
            const parsedValue = Array.isArray(value.fieldValue)
                ? value.fieldValue.join('|')
                : value.fieldValue
            formData.append(`values[${index}][formField]`, value.formField)
            formData.append(`values[${index}][fieldValue]`, parsedValue)
        })

        try {
            axios
                .post(
                    `${API_URL}${API_URL[API_URL.length - 1] === '/' ? '' : '/'}form-submissions`,
                    formData,
                    { headers }
                )
                .then(res => {
                    if (form?.redirectUrl?.length > 0) {
                        window.location = form.redirectUrl
                    }
                })
        } catch (e) {
            console.error(e)
            const violations = e?.error?.body?.violations
            if (violations?.length > 0) {
                setErrorMessages(violations.map(v => v.message))
            } else {
                setErrorMessages(['There was an error submitting this form.'])
            }
        }
    }

    if (formData) {
        const { formFieldLinks } = formData

        const values = {}

        const queryString = qs.parse(typeof window === 'undefined' ? '' : window.location.search)

        formFieldLinks.forEach(field => {
            const fromQueryParam = queryString[field.thirdPartyFieldId] || false

            values[field['@id']] =
                fromQueryParam || field.defaultValue || getDefaultInput(field.inputType)
        })

        return (
            <>
                <RecaptchaForm
                    onSubmit={data => handleSubmit(formData, data)}
                    initialValues={values}
                    enableReinitialize={true}
                    className="content__form"
                    setReCaptchaToken={setReCaptchaToken}
                    reCaptchaToken={reCaptchaToken}
                >
                    {() => (
                        <FormikForm>
                            {formData.label.length > 0 && !hideTitle && (
                                <h3 className="font-bold mb-[15px] content__form__heading">
                                    {formData.label}
                                </h3>
                            )}
                            {formFieldLinks.map((field, idx) => {
                                let input
                                let inputType = 'text'
                                let fieldName = field['@id']
                                let label = field.label
                                const defaultValue = field.defaultValue
                                const isRequired = field.mandatory

                                switch (field.inputType) {
                                    case 'checkbox':
                                        input = (
                                            <Checkbox
                                                name={fieldName}
                                                label={label}
                                                help={field.helpText ? field.helpText : false}
                                                required={isRequired}
                                            />
                                        )
                                        break

                                    case 'multi-select':
                                        input = (
                                            <CheckboxGroup
                                                name={fieldName}
                                                label={label}
                                                options={field.inputOptions.map(opt => ({
                                                    label: opt,
                                                    value: opt,
                                                }))}
                                                help={field.helpText ? field.helpText : false}
                                                required={isRequired}
                                            />
                                        )
                                        break

                                    case 'select':
                                        input = (
                                            <Select
                                                name={fieldName}
                                                label={label}
                                                options={field.inputOptions.map(opt => ({
                                                    label: opt,
                                                    value: opt,
                                                }))}
                                                defaultValue={defaultValue}
                                                help={field.helpText ? field.helpText : false}
                                                required={isRequired}
                                            />
                                        )
                                        break

                                    case 'file':
                                        input = (
                                            <File
                                                name={fieldName}
                                                label={label}
                                                help={field.helpText ? field.helpText : false}
                                                required={isRequired}
                                            />
                                        )
                                        break

                                    case 'radio':
                                        input = (
                                            <Radio
                                                name={fieldName}
                                                label={label}
                                                options={field.inputOptions.map(opt => ({
                                                    label: opt,
                                                    value: opt,
                                                }))}
                                                help={field.helpText ? field.helpText : false}
                                                required={isRequired}
                                                defaultValue={values[fieldName] || ''}
                                            />
                                        )
                                        break

                                    case 'textarea':
                                        input = (
                                            <Textarea
                                                name={fieldName}
                                                label={label}
                                                type={inputType}
                                                help={field.helpText ? field.helpText : false}
                                                required={isRequired}
                                                defaultValue={values[fieldName] || ''}
                                            />
                                        )
                                        break

                                    case 'email':
                                        inputType = 'email'
                                        break

                                    case 'date':
                                        inputType = 'date'
                                        break

                                    case 'telephone':
                                        inputType = 'tel'
                                        break

                                    case 'hidden':
                                        inputType = 'hidden'
                                        break
                                }

                                if (!input) {
                                    input = (
                                        <Input
                                            name={fieldName}
                                            label={label}
                                            placeholder={label}
                                            type={inputType}
                                            help={field.helpText ? field.helpText : false}
                                            required={isRequired}
                                            defaultValue={values[fieldName] || ''}
                                        />
                                    )
                                }

                                return (
                                    <React.Fragment key={`${label}_${idx}`}>{input}</React.Fragment>
                                )
                            })}

                            <Button type="submit">{formData.submitButtonText || 'Submit'}</Button>

                            {errorMessages?.length > 0 &&
                                errorMessages.map((msg, idx) => (
                                    <p className="text-brand-danger" key={`err_${idx}`}>
                                        {msg}
                                    </p>
                                ))}
                        </FormikForm>
                    )}
                </RecaptchaForm>
            </>
        )
    } else {
        return null
    }
}

FormGenerator.propTypes = {
    formData: PropTypes.object,
}

export default FormGenerator
